import { memo } from 'react';

import { parseFontVariant } from '@air/utils-fonts';
import { cn } from '@air/utils-tailwind';

import { DesignPreviewCard } from '~/components/ShareModal/components/designPreview/DesignPreviewCard';
import { DesignPreviewCover } from '~/components/ShareModal/components/designPreview/DesignPreviewCover';
import { DesignPreviewHeaderBar } from '~/components/ShareModal/components/designPreview/DesignPreviewHeaderBar';
import { DesignPreviewSearchField } from '~/components/ShareModal/components/designPreview/DesignPreviewSearchField';
import { DesignPreviewThumbnailStrip } from '~/components/ShareModal/components/designPreview/DesignPreviewThumbnailStrip';
import { DesignPreviewTitleRow } from '~/components/ShareModal/components/designPreview/DesignPreviewTitleRow';
import { DesignPreviewWorkspaceIdentity } from '~/components/ShareModal/components/designPreview/DesignPreviewWorkspaceIdentity';
import { useShareBoardDesign } from '~/components/SharePrivateBoardModal/ShareBoardDesignProvider';
import { type ShareLinkPageWidth } from '~/components/v2/components/shared/share-link-design/shareLinkPageWidth';
import { getContrastColor } from '~/components/v2/utils/filters/getContrastColor';
import { useFontLoader } from '~/hooks/useFontLoader';

/**
 * Content-column width per page-width choice — mirrors the collection preview's approximation: the
 * mini preview card is a fixed width, so `standard` keeps the original `w-[93%]` and narrow/wide
 * nudge the inner column in/out.
 */
const PAGE_WIDTH_CLASS: Record<ShareLinkPageWidth, string> = {
  narrow: 'w-[80%]',
  standard: 'w-[93%]',
  wide: 'w-full',
};

/**
 * Miniature mocked rendering of the public board share page, shown inside the share modal's Design
 * tab so users see their color choices live. Renders the colors + logo + the board's cover band +
 * title + search + thumbnails composition at auto height, with the page-width and thumbnail
 * corner-rounding layout choices reflected the same way the collection preview approximates them.
 * The cover sits BELOW the header bar, above the content. Foreground colors are derived from the
 * picked backgrounds because the backend stores no foreground colors by design.
 *
 * Reads its already-resolved values from `useShareBoardDesign()` (the provider resolves
 * custom-vs-default, stored-vs-brand-entry, and stored-vs-default — no resolution or defaulting
 * happens here).
 */
export const ShareBoardDesignPreview = memo(() => {
  const {
    boardTitle,
    buttonHex: buttonColor,
    coverSrc: coverImageSrc,
    fontFaceUrl,
    fontFamily,
    fontVariant,
    headerHex: bannerColor,
    isDark,
    logoSrc: customLogoSrc,
    pageHex: backgroundColor,
    previewPageWidth: pageWidth,
    previewThumbnailRadius: thumbnailRadius,
    workspaceLogoSrc,
    workspaceName,
  } = useShareBoardDesign();

  // Setting `font-family` on the title only works if the font is actually in the document — the
  // modal never mounts the public page's loader, so the preview loads its own selected font.
  useFontLoader(fontFamily ? { fontFamily, variant: fontVariant, fontFaceUrl } : undefined);

  // The loader above requests exactly the picked variant's face, so the title must APPLY that same
  // weight/style or the browser synthesizes one — the public page's headline surfaces read the same
  // pair from CSS vars. No variant → undefined, and the title keeps its own `font-bold`.
  const { fontStyle, fontWeight } = parseFontVariant(fontVariant);
  const titleFontVariantStyle = fontFamily && fontVariant ? { fontStyle, fontWeight } : undefined;

  return (
    <DesignPreviewCard backgroundColor={backgroundColor} data-testid="SHARE_DESIGN_TAB_PREVIEW">
      <DesignPreviewHeaderBar bannerColor={bannerColor}>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <DesignPreviewWorkspaceIdentity
            customLogoSrc={customLogoSrc}
            foregroundColor={getContrastColor(bannerColor)}
            workspaceLogoSrc={workspaceLogoSrc}
            workspaceName={workspaceName}
          />
        </div>
        <DesignPreviewSearchField isDark={isDark} />
      </DesignPreviewHeaderBar>

      {/* Page-width is approximated via the inner column width — the card itself is fixed-width. */}
      <div
        className={cn(
          'mx-auto flex shrink-0 flex-col',
          PAGE_WIDTH_CLASS[pageWidth],
          isDark ? 'ads:bg-ads-gray-12 bg-gray-12' : 'ads:bg-ads-gray-1 bg-gray-1',
        )}
      >
        {coverImageSrc ? (
          <DesignPreviewCover
            backgroundColor={backgroundColor}
            coverFade={false}
            coverImageSrc={coverImageSrc}
            coverWidth="fullWidth"
          />
        ) : null}
        <div className="px-3 pt-2 pb-2">
          <DesignPreviewTitleRow
            buttonColor={buttonColor}
            fontFamily={fontFamily}
            title={boardTitle}
            {...titleFontVariantStyle}
          />
          <DesignPreviewThumbnailStrip isDark={isDark} thumbnailRadius={thumbnailRadius} />
        </div>
      </div>
    </DesignPreviewCard>
  );
});

ShareBoardDesignPreview.displayName = 'ShareBoardDesignPreview';
